import { useEffect, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import { searchModels, createBrokerModel } from '@/services/aircraft-identity.service.js';

const btn = 'inline-flex items-center rounded-[5px] border border-accent bg-accent px-2.5 py-1 text-[12px] text-white hover:brightness-110 disabled:opacity-50';
const field = 'rounded-[5px] border border-border bg-surface px-2 py-1 text-[13px] text-text';

// "None of these: state the model" (David 2026-10-31): a model the AIA catalog has no correct entry for, named by
// David (make, model, the display name screens show), saved as his and usable wherever an AMIN model is.
function NameModel({ onNamed, onCancel }) {
  const [make, setMake] = useState(''); const [model, setModel] = useState(''); const [display, setDisplay] = useState('');
  const [saving, setSaving] = useState(false);
  const shown = display.trim() || [make.trim(), model.trim()].filter(Boolean).join(' ');
  const ready = make.trim() && model.trim() && shown;
  const save = async () => {
    setSaving(true);
    try {
      const out = await createBrokerModel({ make, model, display_name: shown });
      toast.success(`${out.model.label} is saved as your model`);
      onNamed(out.model);
    } catch (e) { toast.error(`Not saved: ${e?.body?.error || e?.message || 'failed'}`); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-1.5 rounded-[5px] border border-border p-2">
      <div className="text-[12.5px] text-text-muted">State the model: it is saved as yours and used like any AMIN model.</div>
      <div className="flex flex-wrap gap-2">
        <input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Make (Piper)" className={`${field} w-32`} />
        <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model (PA-32R-301T)" className={`${field} w-44`} />
        <input value={display} onChange={(e) => setDisplay(e.target.value)} placeholder={shown || 'Display name (Piper PA-32R-301T Turbo Saratoga)'} className={`${field} min-w-[16rem] flex-1`} />
      </div>
      <div className="flex gap-2">
        <button type="button" className={btn} disabled={!ready || saving} onClick={save}>{saving ? 'Saving' : ready ? `Save "${shown}" as my model` : 'Name the make and model'}</button>
        <button type="button" className="text-[12px] text-text-muted underline" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// Pick an AMIN model by name (MAKE-MODEL-TIME-SPEC section 7): the catalog's names, ranked by text only. The key is
// the option's value; the screen shows the make and model name, never a code.
export default function ModelSearch({ onPick, placeholder = 'Search the AMIN models by name', autoFocus = false }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [naming, setNaming] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    let live = true; // a slower, older answer never overwrites a newer query's rows
    const t = setTimeout(() => {
      searchModels(q).then((r) => { if (live) { setRows(r.models || []); setErr(null); } }).catch((e) => { if (live) setErr(e?.body?.error || e?.message || 'search failed'); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="space-y-1">
      <input autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
        className="w-full rounded-[5px] border border-border bg-surface px-2 py-1 text-[13px] text-text" />
      {err ? <div className="text-[12px] text-danger">Could not search: {err}</div> : null}
      {rows.length ? (
        <ul className="max-h-48 overflow-y-auto rounded-[5px] border border-border">
          {rows.map((m) => (
            <li key={m.model_key}>
              <button type="button" onClick={() => { onPick(m); setQ(''); setRows([]); }} className="block w-full px-2 py-1 text-left text-[13px] text-text hover:bg-accent-soft/40">
                {m.label}{m.broker ? <span className="text-text-dim"> (yours)</span> : m.first_year ? <span className="text-text-dim"> ({m.first_year}{m.last_year && m.last_year !== m.first_year ? ` to ${m.last_year}` : ''})</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {naming ? <NameModel onNamed={(m) => { setNaming(false); setQ(''); setRows([]); onPick(m); }} onCancel={() => setNaming(false)} />
        : <button type="button" className="text-[12px] text-accent underline" onClick={() => setNaming(true)}>None of these: state the model</button>}
    </div>
  );
}
